import {MDIntent} from '@mintdata-wsp/common/components/MDIntent';
import {SpreadsheetsMainController as BaseSpreadsheetsMainController} from '@mintdata-wsp/common/controllers/spreadsheet/SpreadsheetsMainController';
import {MeshInputState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshInputState';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SpreadsheetEditorState} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetEditorState';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {TextInputDialogButtonSet, TextInputDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/TextInputDialog';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {action, observable} from 'mobx';

class ErrorHolder
{
    @observable
    public error:string = '';

    @action
    setError(error)
    {
        this.error = error;
    }
}

export class SpreadsheetsMainController extends BaseSpreadsheetsMainController
{
    // strange name, but this is what I need
    public findAnySheetByIdWithItsMainSheetId(sheetId:SheetId, includeFrameDetailsSheets:boolean = false):{mainSheetId:SheetId, sheet:AnySheet | null}
    {

        const {frameDetails} = controllers.ui.spreadsheets;
        const state = this._getSpreadsheetState();

        if( !state )
            return null;

        const workbook = state.sheetToWorkbookMap.get(sheetId) || null;
        if( workbook )
        {
            const sheet = workbook.runtime.sheetsMap.get(sheetId);
            return sheet && {sheet, mainSheetId: sheetId};
        }

        if( includeFrameDetailsSheets )
        {
            const workbooksMap = this._getSpreadsheetState().workbooks;
            for( const workbook of Array.from(workbooksMap.values()) )
            {
                const sheets = workbook.runtime.sheets;
                for( const mainSheet of sheets )
                {
                    const frameDetailsEntry = frameDetails.getEntryBySheetId(workbook.id, mainSheet.id, sheetId);
                    if( frameDetailsEntry )
                        return {mainSheetId: mainSheet.id, sheet: frameDetailsEntry.sheet};
                }
            }
        }

        return null;
    }

    public findAnySheetById(sheetId:SheetId, includeFrameDetailsSheets:boolean = false):AnySheet | null
    {
        const result = this.findAnySheetByIdWithItsMainSheetId(sheetId, includeFrameDetailsSheets);

        return result && result.sheet;
    }

    public getActiveSheet():AnySheet | null
    {
        const {tabs} = controllers.ui.spreadsheets;
        const workbookIds:Array<WorkbookId> = this.getAllWorkbookIds();

        return workbookIds.length > 0 ? tabs.getActiveSheet(workbookIds[0]) : null;
    }

    public toggleNamedRangesVisible(ctx:IWSPMeshContext):void
    {
        const workbook = this.findWorkbookById(ctx.workbookId);
        workbook.view.areNamedRangesVisible = !workbook.view.areNamedRangesVisible;
    }

    public areNamedRangesVisible(ctx:IWSPMeshContext):boolean
    {
        const workbook = this.findWorkbookById(ctx.workbookId);
        return workbook.view.areNamedRangesVisible;
    }

    public createNewNamedRange(ctx:IWSPMeshContext):void
    {
        const {mesh, workbook: workbookCtrl, namedRanges, frameDetails} = controllers.ui.spreadsheets;
        const {workbookId, sheetId, mainSheetId, sheetCtrl} = ctx;

        const workbook = this.getRootWorkbook();
        const spreadsheetRuntimeState = workbook.runtime;

        const meshContext = workbookCtrl.getMeshContextBySheetId(sheetId);
        const meshState = mesh.getState(meshContext);

        const selectedRange = meshState.details.selectedRange;
        if( !selectedRange )
            return;

        const errorHolder = new ErrorHolder();
        const props = new TextInputDialogProps(
            MDIntent.Default,
            `Create named range`,
            null,
            'Name',
            'Enter name',
            '',
            null,
            TextInputDialogButtonSet.OK_CANCEL,
            (newName) =>
            {
                if( namedRanges.getNamedRangeByName(newName) )
                {
                    errorHolder.setError(`Range with that name already exist`);
                    return false;
                }

                if( !/^([a-zA-Z][a-zA-Z0-9]*)$/.test(newName) )
                {
                    errorHolder.setError(`Invalid name format`);
                    return false;
                }

                const {from, to} = selectedRange;
                const {mainLocation, framePath} = frameDetails.computeFramePathByContext(ctx, from.toSlotLocation());
                namedRanges.createNamedRange(
                    workbookId,
                    mainSheetId,
                    mainLocation,
                    framePath,
                    to.rowIndex - from.rowIndex + 1,
                    to.colIndex - from.colIndex + 1,
                    newName
                );

                mesh.focusMesh(meshContext);

                return true;
            },
            null,
            false,
            () => errorHolder.setError(''),
            () => errorHolder.error
        );

        controllers.ui.dialogs.showDialog(Dialogs.TextInput, false, props);
    }

    public getEditorState():SpreadsheetEditorState
    {
        const workbook = this.getRootWorkbook();

        return workbook.editor;
    }

    public get isEditMode()
    {
        return this.getEditorState().isEditMode;
    }

    @action
    public setEditMode(isEditMode:boolean, sheetId:string = null)
    {
        const editorState = this.getEditorState();

        editorState.isEditMode = isEditMode;
        editorState.editSheetId = sheetId;

        if( !isEditMode )
            editorState.isShowCellRefHint = false;
    }

    @action
    public setShowCellRefHint(isShowCellRefHint:boolean)
    {
        this.getEditorState().isShowCellRefHint = isShowCellRefHint;
    }
}
